import React, { useEffect, useState, useContext, useRef } from "react";
import {
  makeStyles,
  useTheme,
  Grid,
  FormControl,
  TextField,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Checkbox
} from "@material-ui/core";
import {
  Palette,
  WbSunny,
  NightsStay,
  ColorLens,
  AttachFile,
  Delete,
  Image,
  Language,
  Business,
  CheckCircle,
  Colorize
} from "@material-ui/icons";

import useSettings from "../../hooks/useSettings";
import { toast } from "react-toastify";
import OnlyForSuperUser from "../OnlyForSuperUser";
import useAuth from "../../hooks/useAuth.js";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

import defaultLogoLight from "../../assets/logo.png";
import defaultLogoDark from "../../assets/logo-black.png";
import defaultLogoFavicon from "../../assets/favicon.ico";
import ColorBoxModal from "../ColorBoxModal/index.js";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    container: {
      paddingTop: 0,
      paddingBottom: theme.spacing(4),
      width: "100%",
    },
    topBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: isLight ? "#FFFFFF" : "#16191E",
      border: isLight ? "1px solid #E2E8F0" : "1px solid #262B33",
      boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.05)" : "none",
      borderRadius: 10,
      padding: "14px 20px",
      marginBottom: theme.spacing(2.5),
      flexWrap: "wrap",
      gap: theme.spacing(1.5),
    },
    topBarLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    },
    topBarTitle: {
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: "0.95rem",
      letterSpacing: "0.5px",
      display: "flex",
      alignItems: "center",
      gap: 8,
      textTransform: "uppercase",
    },
    topBarIcon: {
      color: theme.palette.primary.main,
      fontSize: "1.3rem",
    },
    chipBadge: {
      backgroundColor: `${theme.palette.primary.main}14`,
      border: `1px solid ${theme.palette.primary.main}4D`,
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: "0.75rem",
      height: 26,
      borderRadius: 13,
      padding: "0 8px",
    },
    sectionPaper: {
      backgroundColor: isLight ? "#FFFFFF" : "#16191E",
      border: isLight ? "1px solid #E2E8F0" : "1px solid #262B33",
      boxShadow: isLight ? "0 1px 4px rgba(0,0,0,0.05)" : "none",
      borderRadius: 10,
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      marginBottom: theme.spacing(2.5),
    },
    sectionIcon: {
      marginRight: theme.spacing(1.5),
      color: theme.palette.primary.main,
      fontSize: "1.5rem",
    },
    sectionTitle: {
      fontWeight: 700,
      color: isLight ? "#0F172A" : "#FFFFFF",
      fontSize: "1rem",
      letterSpacing: "0.3px",
    },
    sectionSubtitle: {
      color: isLight ? "#64748B" : "#8C939D",
      fontSize: "0.82rem",
      marginTop: 2,
    },

    /* Tarjetas de Modo (Light / Dark) */
    modeCard: {
      backgroundColor: isLight ? "#F8FAFC" : "#111317",
      border: isLight ? "1px solid #E2E8F0" : "1px solid #262B33",
      borderRadius: 10,
      padding: theme.spacing(2),
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      transition: "all 0.25s ease-in-out",
      "&:hover": {
        borderColor: `${theme.palette.primary.main}80`,
        transform: "translateY(-2px)",
        boxShadow: isLight ? "0 4px 12px rgba(0,0,0,0.08)" : "0 4px 14px rgba(0, 0, 0, 0.35)",
      },
    },
    modeCardActive: {
      backgroundColor: isLight ? "#F0FDFA" : "#171B22",
      borderColor: `${theme.palette.primary.main} !important`,
      boxShadow: `0 0 16px ${theme.palette.primary.main}33`,
    },
    modeCardContent: {
      display: "flex",
      alignItems: "center",
      gap: 14,
    },
    modeIconLight: {
      color: "#FFA726",
      fontSize: "2rem",
    },
    modeIconDark: {
      color: theme.palette.primary.main,
      fontSize: "2rem",
    },
    modeTitleText: {
      color: isLight ? "#0F172A" : "#FFFFFF",
      fontWeight: 700,
      fontSize: "0.95rem",
    },
    modeDescText: {
      color: isLight ? "#64748B" : "#8C939D",
      fontSize: "0.78rem",
      marginTop: 2,
    },
    activeChip: {
      backgroundColor: `${theme.palette.primary.main}1F`,
      border: `1px solid ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: "0.72rem",
      height: 24,
      borderRadius: 12,
    },

    /* Tarjetas de Colores de Acento */
    accentGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
      gap: theme.spacing(2),
    },
    accentCard: {
      backgroundColor: isLight ? "#FFFFFF" : "#111317",
      border: isLight ? "1px solid #E2E8F0" : "1px solid #262B33",
      borderRadius: 10,
      padding: theme.spacing(2),
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      minHeight: 140,
      position: "relative",
      overflow: "hidden",
      transition: "all 0.25s ease-in-out",
      boxShadow: isLight ? "0 1px 3px rgba(0,0,0,0.04)" : "none",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: isLight ? "0 4px 14px rgba(0,0,0,0.08)" : "0 6px 18px rgba(0, 0, 0, 0.4)",
      },
    },
    accentCardHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: theme.spacing(1.2),
    },
    accentCircle: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
    },
    accentCardTitle: {
      color: isLight ? "#0F172A" : "#FFFFFF",
      fontWeight: 700,
      fontSize: "0.92rem",
      marginBottom: 4,
    },
    accentCardDesc: {
      color: isLight ? "#64748B" : "#8C939D",
      fontSize: "0.76rem",
      lineHeight: 1.35,
      flexGrow: 1,
      marginBottom: 10,
    },
    accentBottomBar: {
      height: 4,
      borderRadius: 2,
      width: "100%",
    },

    /* Inputs y Formularios */
    formField: {
      marginBottom: theme.spacing(2),
      "& .MuiOutlinedInput-root": {
        backgroundColor: isLight ? "#FFFFFF !important" : "#1e1e1e !important",
        borderRadius: 8,
        "& fieldset": {
          borderColor: isLight ? "rgba(0, 0, 0, 0.15)" : "rgba(255, 255, 255, 0.15)",
        },
        "&:hover fieldset": {
          borderColor: `${theme.palette.primary.main}80`,
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
        },
      },
      "& .MuiInputBase-input": {
        color: isLight ? "#0F172A" : "#FFFFFF",
        fontFamily: "'Inter', sans-serif",
      },
      "& .MuiInputLabel-root": {
        color: isLight ? "#64748B" : "#8C939D",
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
    customColorBtn: {
      border: `1px dashed ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      padding: "6px 14px",
      borderRadius: 8,
      textTransform: "none",
      fontWeight: 600,
      fontSize: "0.82rem",
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}14`,
        borderColor: theme.palette.primary.main,
      },
    },

    /* Logos y Previsualizaciones */
    uploadInput: {
      display: "none",
    },
    uploadField: {
      "& .MuiInputBase-input": {
        cursor: "pointer",
      },
    },
    previewCard: {
      backgroundColor: isLight ? "#F8FAFC" : "#111317",
      border: isLight ? "1px solid #E2E8F0" : "1px solid #262B33",
      borderRadius: 8,
      textAlign: "center",
      height: 120,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      padding: theme.spacing(1.5),
      position: "relative",
    },
    previewImage: {
      maxWidth: "100%",
      maxHeight: 70,
      objectFit: "contain",
    },
    previewBackgroundImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: 4,
    },
    previewLabel: {
      fontSize: "0.75rem",
      color: isLight ? "#64748B" : "#8C939D",
      marginTop: 6,
      fontWeight: 600,
    },

    /* Idiomas */
    languageGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1.5),
    },
    languageItem: {
      display: "flex",
      alignItems: "center",
      backgroundColor: isLight ? "#F8FAFC" : "#111317",
      border: isLight ? "1px solid #E2E8F0" : "1px solid #262B33",
      borderRadius: 8,
      padding: "6px 14px",
      minWidth: 140,
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: `${theme.palette.primary.main}66`,
      },
    },
    languageLabel: {
      fontSize: "0.85rem",
      fontWeight: 600,
      color: isLight ? "#0F172A" : "#FFFFFF",
      marginLeft: 6,
    },
    languageCheckbox: {
      padding: 2,
      color: isLight ? "#94A3B8" : "#8C939D",
      "&.Mui-checked": {
        color: theme.palette.primary.main,
      },
    },
  };
});

const LANGUAGE_OPTIONS = [
  { code: "pt-BR", label: "Português" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "ar", label: "عربي" },
];

const ACCENT_PRESETS = [
  {
    id: "cyan",
    hex: "#00E5FF",
    nameKey: "colors.cyan.name",
    descKey: "colors.cyan.desc",
    gradient: "linear-gradient(90deg, #00E5FF 0%, #00B4D8 100%)",
  },
  {
    id: "violet",
    hex: "#8B5CF6",
    nameKey: "colors.violet.name",
    descKey: "colors.violet.desc",
    gradient: "linear-gradient(90deg, #8B5CF6 0%, #6D28D9 100%)",
  },
  {
    id: "emerald",
    hex: "#10B981",
    nameKey: "colors.emerald.name",
    descKey: "colors.emerald.desc",
    gradient: "linear-gradient(90deg, #10B981 0%, #059669 100%)",
  },
  {
    id: "cobalt",
    hex: "#2563EB",
    nameKey: "colors.cobalt.name",
    descKey: "colors.cobalt.desc",
    gradient: "linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%)",
  },
  {
    id: "amber",
    hex: "#F59E0B",
    nameKey: "colors.amber.name",
    descKey: "colors.amber.desc",
    gradient: "linear-gradient(90deg, #F59E0B 0%, #D97706 100%)",
  },
  {
    id: "rose",
    hex: "#F43F5E",
    nameKey: "colors.rose.name",
    descKey: "colors.rose.desc",
    gradient: "linear-gradient(90deg, #F43F5E 0%, #BE123C 100%)",
  },
];

export default function Whitelabel(props) {
  const { settings } = props;
  const classes = useStyles();
  const theme = useTheme();
  const [settingsLoaded, setSettingsLoaded] = useState({});

  const { getCurrentUserInfo } = useAuth();
  const [currentUser, setCurrentUser] = useState({});
  const [loading, setLoading] = useState(true);
  const { colorMode } = useContext(ColorModeContext);

  const [customColorModalOpen, setCustomColorModalOpen] = useState(false);

  const logoLightInput = useRef(null);
  const logoDarkInput = useRef(null);
  const logoFaviconInput = useRef(null);
  const backgroundLightInput = useRef(null);
  const backgroundDarkInput = useRef(null);
  const appNameInput = useRef(null);
  const [appName, setAppName] = useState(settingsLoaded.appName || "");
  const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);

  const { update } = useSettings();

  function updateSettingsLoaded(key, value) {
    if (
      key === "primaryColorLight" ||
      key === "primaryColorDark" ||
      key === "appName"
    ) {
      localStorage.setItem(key, value);
    }
    const newSettings = { ...settingsLoaded };
    newSettings[key] = value;
    setSettingsLoaded(newSettings);
  }

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    getCurrentUserInfo().then((u) => {
      const userData = u.user || u;
      if (isMounted) {
        setCurrentUser(userData);
      }
    });

    if (Array.isArray(settings) && settings.length) {
      const primaryColorLight = settings.find(
        (s) => s.key === "primaryColorLight"
      )?.value;
      const primaryColorDark = settings.find(
        (s) => s.key === "primaryColorDark"
      )?.value;
      const appLogoLight = settings.find(
        (s) => s.key === "appLogoLight"
      )?.value;
      const appLogoDark = settings.find((s) => s.key === "appLogoDark")?.value;
      const appLogoFavicon = settings.find(
        (s) => s.key === "appLogoFavicon"
      )?.value;
      const appLogoBackgroundLight = settings.find(
        (s) => s.key === "appLogoBackgroundLight"
      )?.value;
      const appLogoBackgroundDark = settings.find(
        (s) => s.key === "appLogoBackgroundDark"
      )?.value;
      const appName = settings.find((s) => s.key === "appName")?.value;
      const enabledLanguagesSetting = settings.find(
        (s) => s.key === "enabledLanguages"
      )?.value;
      let langs = ["pt-BR", "en"];
      try {
        if (enabledLanguagesSetting) {
          langs = JSON.parse(enabledLanguagesSetting);
        }
      } catch {}

      if (isMounted) {
        setAppName(appName || "");
        setEnabledLanguages(langs);
        setSettingsLoaded({
          ...settingsLoaded,
          primaryColorLight: primaryColorLight || "#10B981",
          primaryColorDark: primaryColorDark || "#10B981",
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appLogoBackgroundLight,
          appLogoBackgroundDark,
          appName,
          enabledLanguages: langs,
        });
        setLoading(false);
      }
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, []);

  async function handleSaveSetting(key, value) {
    await update({
      key,
      value,
    });
    updateSettingsLoaded(key, value);
    toast.success(i18n.t("whitelabel.saveSuccess", "¡Configuración de apariencia guardada exitosamente!"));
  }

  const handleSelectMode = async (modeName) => {
    if (colorMode && colorMode.setMode) {
      colorMode.setMode(modeName);
    } else if (colorMode && colorMode.toggleColorMode) {
      if (colorMode.mode !== modeName) {
        colorMode.toggleColorMode();
      }
    }

    if (currentUser && currentUser.id) {
      try {
        await api.put(`/users/${currentUser.id}`, { defaultTheme: modeName });
      } catch (e) {
        console.warn("No se pudo persistir defaultTheme en backend:", e);
      }
    }
  };

  const handleSelectAccentColor = async (hex) => {
    try {
      localStorage.setItem("primaryColorLight", hex);
      localStorage.setItem("primaryColorDark", hex);
      updateSettingsLoaded("primaryColorLight", hex);
      updateSettingsLoaded("primaryColorDark", hex);

      if (colorMode) {
        if (colorMode.setPrimaryColorLight) colorMode.setPrimaryColorLight(hex);
        if (colorMode.setPrimaryColorDark) colorMode.setPrimaryColorDark(hex);
      }

      if (currentUser && currentUser.id) {
        try {
          await api.put(`/users/${currentUser.id}`, { color: hex });
        } catch (e) {
          console.warn("No se pudo persistir color de usuario en backend:", e);
        }
      }

      try {
        await update({ key: "primaryColorLight", value: hex });
        await update({ key: "primaryColorDark", value: hex });
      } catch (errApi) {
        console.warn("Backend setting update skipped:", errApi);
      }

      toast.success(i18n.t("whitelabel.saveSuccess", "¡Configuración de apariencia guardada exitosamente!"));
    } catch (err) {
      console.error("Error guardando color de acento:", err);
    }
  };

  async function handleSaveEnabledLanguages(newLangs) {
    await handleSaveSetting("enabledLanguages", newLangs);
    setEnabledLanguages(newLangs);
  }

  const uploadLogo = async (e, mode) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    const formData = new FormData();

    formData.append("typeArch", "logo");
    formData.append("mode", mode);
    formData.append("file", file);

    await api
      .post("/settings-whitelabel/logo", formData, {
        onUploadProgress: (event) => {
          let progress = Math.round((event.loaded * 100) / event.total);
          console.log(`A imagem está ${progress}% carregada... `);
        },
      })
      .then((response) => {
        updateSettingsLoaded(`appLogo${mode}`, response.data);
        if (mode === "BackgroundLight" || mode === "BackgroundDark") {
        } else {
          colorMode[`setAppLogo${mode}`](
            getBackendUrl() + "/public/" + response.data
          );
        }
      })
      .catch((err) => {
        console.error(`Houve um problema ao realizar o upload da imagem.`, err);
      });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Typography style={{ color: theme.palette.primary.main }}>{i18n.t("settings.loading")}</Typography>
      </div>
    );
  }

  const currentMode = colorMode?.mode || "dark";
  const isLight = currentMode === "light";
  const currentAccent =
    settingsLoaded.primaryColorDark ||
    settingsLoaded.primaryColorLight ||
    "#10B981";

  const matchedPreset = ACCENT_PRESETS.find(
    (p) => p.hex.toLowerCase() === currentAccent.toLowerCase()
  );
  const currentAccentName = matchedPreset
    ? i18n.t(`whitelabel.${matchedPreset.nameKey}`)
    : i18n.t("whitelabel.colors.custom.name", "Personalizado");

  return (
    <div className={classes.container}>
      {/* TOP BAR CON TÍTULO Y CHIPS DE ESTADO */}
            <Paper className={classes.topBar} elevation={0}>
              <div className={classes.topBarLeft}>
                <Typography className={classes.topBarTitle}>
                  <Palette className={classes.topBarIcon} />
                  {i18n.t("whitelabel.title", "Apariencia y Personalización (Whitelabel)")}
                </Typography>
                <Chip
                  className={classes.chipBadge}
                  label={`${i18n.t("whitelabel.modeTitle")}: ${
                    currentMode === "dark"
                      ? i18n.t("whitelabel.modes.dark")
                      : i18n.t("whitelabel.modes.light")
                  }`}
                  size="small"
                />
                <Chip
                  className={classes.chipBadge}
                  label={`${i18n.t("whitelabel.accentColorTitle")}: ${currentAccentName}`}
                  size="small"
                />
              </div>
            </Paper>

            {/* SECCIÓN 1: MODO DE VISUALIZACIÓN (LIGHT / DARK) */}
            <Paper className={classes.sectionPaper} elevation={0}>
              <div className={classes.sectionHeader}>
                <WbSunny className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whitelabel.modeTitle", "Modo de Visualización")}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {i18n.t(
                      "whitelabel.subtitle",
                      "Define el modo y el color de acento que se usan en toda la app. Se guarda en este dispositivo; pruébalo, cambia al instante."
                    )}
                  </Typography>
                </div>
              </div>

              <Grid container spacing={2}>
                {/* TARJETA LIGHT */}
                <Grid item xs={12} sm={6}>
                  <div
                    className={`${classes.modeCard} ${
                      currentMode === "light" ? classes.modeCardActive : ""
                    }`}
                    onClick={() => handleSelectMode("light")}
                  >
                    <div className={classes.modeCardContent}>
                      <WbSunny className={classes.modeIconLight} />
                      <div>
                        <Typography className={classes.modeTitleText}>
                          {i18n.t("whitelabel.modes.light", "Light / Claro")}
                        </Typography>
                        <Typography className={classes.modeDescText}>
                          {i18n.t(
                            "whitelabel.modes.lightDesc",
                            "Interfaz diurna luminosa para espacios con mucha claridad."
                          )}
                        </Typography>
                      </div>
                    </div>
                    {currentMode === "light" && (
                      <Chip
                        icon={<CheckCircle style={{ color: theme.palette.primary.main, fontSize: "0.95rem" }} />}
                        label={i18n.t("whitelabel.active", "Activo")}
                        className={classes.activeChip}
                        size="small"
                      />
                    )}
                  </div>
                </Grid>

                {/* TARJETA DARK */}
                <Grid item xs={12} sm={6}>
                  <div
                    className={`${classes.modeCard} ${
                      currentMode === "dark" ? classes.modeCardActive : ""
                    }`}
                    onClick={() => handleSelectMode("dark")}
                  >
                    <div className={classes.modeCardContent}>
                      <NightsStay className={classes.modeIconDark} />
                      <div>
                        <Typography className={classes.modeTitleText}>
                          {i18n.t("whitelabel.modes.dark", "Dark / Oscuro")}
                        </Typography>
                        <Typography className={classes.modeDescText}>
                          {i18n.t(
                            "whitelabel.modes.darkDesc",
                            "Interfaz glassmórfica oscura de alto contraste, descansada para la vista."
                          )}
                        </Typography>
                      </div>
                    </div>
                    {currentMode === "dark" && (
                      <Chip
                        icon={<CheckCircle style={{ color: theme.palette.primary.main, fontSize: "0.95rem" }} />}
                        label={i18n.t("whitelabel.active", "Activo")}
                        className={classes.activeChip}
                        size="small"
                      />
                    )}
                  </div>
                </Grid>
              </Grid>
            </Paper>

            {/* SECCIÓN 2: PALETA DE COLORES DE ACENTO */}
            <Paper className={classes.sectionPaper} elevation={0}>
              <div className={classes.sectionHeader}>
                <ColorLens className={classes.sectionIcon} />
                <div style={{ flexGrow: 1 }}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whitelabel.accentColorTitle", "Color de Acento")}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {i18n.t(
                      "whitelabel.accentColorSubtitle",
                      "Selecciona el color temático para botones, enlaces, bordes y elementos interactivos."
                    )}
                  </Typography>
                </div>
                <Button
                  className={classes.customColorBtn}
                  startIcon={<Colorize />}
                  onClick={() => setCustomColorModalOpen(true)}
                >
                  {i18n.t("whitelabel.chooseCustomColor", "Elegir Color Libre")}
                </Button>
              </div>

              <div className={classes.accentGrid}>
                {ACCENT_PRESETS.map((preset) => {
                  const isActive =
                    currentAccent.toLowerCase() === preset.hex.toLowerCase();
                  return (
                    <div
                      key={preset.id}
                      className={classes.accentCard}
                      style={{
                        borderColor: isActive ? preset.hex : (currentMode === "light" ? "#E2E8F0" : "#262B33"),
                        boxShadow: isActive ? `0 0 16px ${preset.hex}40` : "none",
                      }}
                      onClick={() => handleSelectAccentColor(preset.hex)}
                    >
                      <div>
                        <div className={classes.accentCardHeader}>
                          <div
                            className={classes.accentCircle}
                            style={{ backgroundColor: preset.hex }}
                          />
                          {isActive && (
                            <Chip
                              icon={
                                <CheckCircle
                                  style={{ color: preset.hex, fontSize: "0.85rem" }}
                                />
                              }
                              label={i18n.t("whitelabel.active", "Activo")}
                              size="small"
                              style={{
                                backgroundColor: `${preset.hex}18`,
                                border: `1px solid ${preset.hex}`,
                                color: preset.hex,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                height: 24,
                                borderRadius: 12,
                              }}
                            />
                          )}
                        </div>
                        <Typography className={classes.accentCardTitle}>
                          {i18n.t(`whitelabel.${preset.nameKey}`)}
                        </Typography>
                        <Typography className={classes.accentCardDesc}>
                          {i18n.t(`whitelabel.${preset.descKey}`)}
                        </Typography>
                      </div>
                      <div
                        className={classes.accentBottomBar}
                        style={{ background: preset.gradient }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* MODAL DE COLOR PERSONALIZADO */}
              <ColorBoxModal
                open={customColorModalOpen}
                handleClose={() => setCustomColorModalOpen(false)}
                onChange={(color) => {
                  const hex = `#${color.hex}`;
                  handleSelectAccentColor(hex);
                }}
                currentColor={currentAccent}
              />
            </Paper>

      {/* SECCIONES EXCLUSIVAS PARA SUPERADMINISTRADOR (LOGOS E IDIOMAS) */}
      <OnlyForSuperUser
        user={currentUser}
        yes={() => (
          <>
            {/* SECCIÓN 3: IDENTIDAD DE MARCA Y LOGOS */}
            <Paper className={classes.sectionPaper} elevation={0}>
              <div className={classes.sectionHeader}>
                <Business className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whitelabel.brandIdentity", "Identidad de Marca y Nombre")}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {i18n.t(
                      "whitelabel.brandIdentityDesc",
                      "Configura el nombre visible de la plataforma, logos institucionales y fondos."
                    )}
                  </Typography>
                </div>
              </div>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="appname-field"
                      label={i18n.t("whitelabel.appName")}
                      variant="outlined"
                      name="appName"
                      value={appName}
                      inputRef={appNameInput}
                      onChange={(e) => setAppName(e.target.value)}
                      onBlur={async () => {
                        await handleSaveSetting("appName", appName);
                        if (colorMode && colorMode.setAppName) {
                          colorMode.setAppName(appName || "BotMix");
                        }
                      }}
                      size="small"
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* LOGOS Y FONDOS */}
              <Grid container spacing={2} style={{ marginTop: 8 }}>
                {/* LOGO LIGHT */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="logo-light-upload-field"
                      label={i18n.t("whitelabel.logoLight")}
                      variant="outlined"
                      value={settingsLoaded.appLogoLight || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoLight && (
                              <IconButton
                                size="small"
                                style={{ color: "#F43F5E" }}
                                onClick={() => {
                                  handleSaveSetting("appLogoLight", "");
                                  colorMode.setAppLogoLight(defaultLogoLight);
                                }}
                              >
                                <Delete titleAccess={i18n.t("whitelabel.delete")} />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-logo-light-button"
                              ref={logoLightInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "Light")}
                            />
                            <label htmlFor="upload-logo-light-button">
                              <IconButton
                                size="small"
                                style={{ color: theme.palette.primary.main }}
                                onClick={() => logoLightInput.current.click()}
                              >
                                <AttachFile titleAccess={i18n.t("whitelabel.upload")} />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                {/* LOGO DARK */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="logo-dark-upload-field"
                      label={i18n.t("whitelabel.logoDark")}
                      variant="outlined"
                      value={settingsLoaded.appLogoDark || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoDark && (
                              <IconButton
                                size="small"
                                style={{ color: "#F43F5E" }}
                                onClick={() => {
                                  handleSaveSetting("appLogoDark", "");
                                  colorMode.setAppLogoDark(defaultLogoDark);
                                }}
                              >
                                <Delete titleAccess={i18n.t("whitelabel.delete")} />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-logo-dark-button"
                              ref={logoDarkInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "Dark")}
                            />
                            <label htmlFor="upload-logo-dark-button">
                              <IconButton
                                size="small"
                                style={{ color: theme.palette.primary.main }}
                                onClick={() => logoDarkInput.current.click()}
                              >
                                <AttachFile titleAccess={i18n.t("whitelabel.upload")} />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                {/* FAVICON */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="logo-favicon-upload-field"
                      label={i18n.t("whitelabel.favicon")}
                      variant="outlined"
                      value={settingsLoaded.appLogoFavicon || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoFavicon && (
                              <IconButton
                                size="small"
                                style={{ color: "#F43F5E" }}
                                onClick={() => {
                                  handleSaveSetting("appLogoFavicon", "");
                                  colorMode.setAppLogoFavicon(defaultLogoFavicon);
                                }}
                              >
                                <Delete titleAccess={i18n.t("whitelabel.delete")} />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-logo-favicon-button"
                              ref={logoFaviconInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "Favicon")}
                            />
                            <label htmlFor="upload-logo-favicon-button">
                              <IconButton
                                size="small"
                                style={{ color: theme.palette.primary.main }}
                                onClick={() => logoFaviconInput.current.click()}
                              >
                                <AttachFile titleAccess={i18n.t("whitelabel.upload")} />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                {/* BACKGROUND LIGHT */}
                <Grid item xs={12} sm={6}>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="background-light-upload-field"
                      label={i18n.t("whitelabel.backgroundLight")}
                      variant="outlined"
                      value={settingsLoaded.appLogoBackgroundLight || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoBackgroundLight && (
                              <IconButton
                                size="small"
                                style={{ color: "#F43F5E" }}
                                onClick={() => {
                                  handleSaveSetting("appLogoBackgroundLight", "");
                                }}
                              >
                                <Delete titleAccess={i18n.t("whitelabel.delete")} />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-background-light-button"
                              ref={backgroundLightInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "BackgroundLight")}
                            />
                            <label htmlFor="upload-background-light-button">
                              <IconButton
                                size="small"
                                style={{ color: theme.palette.primary.main }}
                                onClick={() => backgroundLightInput.current.click()}
                              >
                                <AttachFile titleAccess={i18n.t("whitelabel.upload")} />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>

                {/* BACKGROUND DARK */}
                <Grid item xs={12} sm={6}>
                  <FormControl className={classes.formField} fullWidth>
                    <TextField
                      id="background-dark-upload-field"
                      label={i18n.t("whitelabel.backgroundDark")}
                      variant="outlined"
                      value={settingsLoaded.appLogoBackgroundDark || ""}
                      size="small"
                      className={classes.uploadField}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <>
                            {settingsLoaded.appLogoBackgroundDark && (
                              <IconButton
                                size="small"
                                style={{ color: "#F43F5E" }}
                                onClick={() => {
                                  handleSaveSetting("appLogoBackgroundDark", "");
                                }}
                              >
                                <Delete titleAccess={i18n.t("whitelabel.delete")} />
                              </IconButton>
                            )}
                            <input
                              type="file"
                              id="upload-background-dark-button"
                              ref={backgroundDarkInput}
                              className={classes.uploadInput}
                              onChange={(e) => uploadLogo(e, "BackgroundDark")}
                            />
                            <label htmlFor="upload-background-dark-button">
                              <IconButton
                                size="small"
                                style={{ color: theme.palette.primary.main }}
                                onClick={() => backgroundDarkInput.current.click()}
                              >
                                <AttachFile titleAccess={i18n.t("whitelabel.upload")} />
                              </IconButton>
                            </label>
                          </>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* PREVISUALIZADORES */}
              <div style={{ marginTop: 16 }}>
                <Typography
                  variant="subtitle2"
                  style={{
                    fontWeight: 700,
                    color: isLight ? "#0F172A" : "#FFFFFF",
                    marginBottom: 12,
                  }}
                >
                  {i18n.t("whitelabel.preview")}
                </Typography>
                <Grid container spacing={2}>
                  {/* PREVIEW LOGO LIGHT */}
                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.previewCard} style={{ backgroundColor: "#FFFFFF" }}>
                      <img
                        className={classes.previewImage}
                        src={
                          settingsLoaded.appLogoLight
                            ? getBackendUrl() + "/public/" + settingsLoaded.appLogoLight
                            : defaultLogoLight
                        }
                        alt="Logo Light"
                        onError={(e) => {
                          e.target.src = defaultLogoLight;
                        }}
                      />
                      <div className={classes.previewLabel} style={{ color: "#333" }}>
                        {i18n.t("whitelabel.logoLight")}
                      </div>
                    </div>
                  </Grid>

                  {/* PREVIEW LOGO DARK */}
                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.previewCard}>
                      <img
                        className={classes.previewImage}
                        src={
                          settingsLoaded.appLogoDark
                            ? getBackendUrl() + "/public/" + settingsLoaded.appLogoDark
                            : defaultLogoDark
                        }
                        alt="Logo Dark"
                        onError={(e) => {
                          e.target.src = defaultLogoDark;
                        }}
                      />
                      <div className={classes.previewLabel}>
                        {i18n.t("whitelabel.logoDark")}
                      </div>
                    </div>
                  </Grid>

                  {/* PREVIEW FAVICON */}
                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.previewCard}>
                      <img
                        className={classes.previewImage}
                        style={{ maxHeight: 40 }}
                        src={
                          settingsLoaded.appLogoFavicon
                            ? getBackendUrl() + "/public/" + settingsLoaded.appLogoFavicon
                            : defaultLogoFavicon
                        }
                        alt="Favicon"
                        onError={(e) => {
                          e.target.src = defaultLogoFavicon;
                        }}
                      />
                      <div className={classes.previewLabel}>
                        {i18n.t("whitelabel.favicon")}
                      </div>
                    </div>
                  </Grid>

                  {/* PREVIEW BACKGROUND LIGHT */}
                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.previewCard}>
                      {settingsLoaded.appLogoBackgroundLight ? (
                        <img
                          className={classes.previewBackgroundImage}
                          src={
                            getBackendUrl() +
                            "/public/" +
                            settingsLoaded.appLogoBackgroundLight
                          }
                          alt="Fondo Claro"
                        />
                      ) : (
                        <div className={classes.previewLabel}>
                          {i18n.t("whitelabel.backgroundLight")}
                        </div>
                      )}
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Paper>

            {/* SECCIÓN 4: IDIOMAS DISPONIBLES */}
            <Paper className={classes.sectionPaper} elevation={0}>
              <div className={classes.sectionHeader}>
                <Language className={classes.sectionIcon} />
                <div>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {i18n.t("whitelabel.sections.languages", "Idiomas Disponibles")}
                  </Typography>
                  <Typography className={classes.sectionSubtitle}>
                    {i18n.t(
                      "whitelabel.sections.languagesDescription",
                      "Selecciona qué idiomas estarán disponibles en el sistema."
                    )}
                  </Typography>
                </div>
              </div>

              <div className={classes.languageGrid}>
                {LANGUAGE_OPTIONS.map((lang) => (
                  <div key={lang.code} className={classes.languageItem}>
                    <Checkbox
                      className={classes.languageCheckbox}
                      checked={enabledLanguages.includes(lang.code)}
                      onChange={(e) => {
                        let newLangs = e.target.checked
                          ? [...enabledLanguages, lang.code]
                          : enabledLanguages.filter((c) => c !== lang.code);
                        if (newLangs.length === 0) {
                          toast.error(i18n.t("whitelabel.atLeastOneLanguage"));
                          return;
                        }
                        handleSaveEnabledLanguages(newLangs);
                      }}
                      size="small"
                    />
                    <span className={classes.languageLabel}>{lang.label}</span>
                  </div>
                ))}
              </div>
            </Paper>
          </>
        )}
      />
    </div>
  );
}
